import React from 'react';
import { useIntl } from 'react-intl';
import type { Participation } from 'app/types';
import type { UseQueryRefetch } from 'app/types/useQuery.types';
import { useSnakeBar, useEmployeeDeleteParticipationMutation } from 'app/hooks';
import { TranslationKeys } from 'app/translations';

type UseParticipationsDeleteHandlerProps = {
  connectionId: string | undefined;
  employeeId: string | undefined;
  participationToDelete: Participation | null;
  refetchParticipations: UseQueryRefetch;
  onCloseDeleteDialog: () => void;
};

export const useParticipationsDeleteHandler = ({
  connectionId,
  employeeId,
  participationToDelete,
  refetchParticipations,
  onCloseDeleteDialog,
}: UseParticipationsDeleteHandlerProps) => {
  const intl = useIntl();
  const { showSuccessSnakeBar, showErrorSnakeBar } = useSnakeBar();
  const { mutate: deleteParticipation } = useEmployeeDeleteParticipationMutation();

  const onConfirmDelete = React.useCallback(() => {
    if (!connectionId || !employeeId || !participationToDelete) {
      return;
    }
    const method = 'deleteParticipation';
    deleteParticipation(
      {
        connectionId,
        employeeId,
        participationId: participationToDelete.mongoId,
      },
      {
        onSuccess: () => {
          refetchParticipations();
          showSuccessSnakeBar({
            method,
            message: intl.formatMessage({ id: TranslationKeys.global_update_successMessage }),
          });
          onCloseDeleteDialog();
        },
        onError: () => {
          showErrorSnakeBar({
            method,
            message: intl.formatMessage({ id: TranslationKeys.global_update_errorMessage }),
          });
        },
      },
    );
  }, [
    connectionId,
    employeeId,
    participationToDelete,
    deleteParticipation,
    refetchParticipations,
    showSuccessSnakeBar,
    showErrorSnakeBar,
    onCloseDeleteDialog,
    intl,
  ]);

  return { onConfirmDelete };
};
